import { SerializedStyles, ThemeProvider, css } from "@emotion/react";
import styled from "@emotion/styled";
import * as Sentry from "@sentry/browser";
import { useIsMutating } from "@tanstack/react-query";
import { isHotkey } from "is-hotkey";
import { cloneDeep, noop } from "lodash";
import { useRef, useState } from "react";
import * as React from "react";
import { useSelector } from "react-redux";
import scrollIntoView from "scroll-into-view-if-needed";
import { Descendant, Editor, Element, PointRef, Transforms } from "slate";
import { ReactEditor, useSlate } from "slate-react";

import {
  DesignToken,
  Loader,
  RadiusToken,
  SpacingToken,
  TransitionDurationToken,
} from "@livechat/design-system-react-components";

import Text from "components/layout/Text";
import { RichTextArea } from "components/rich-editor";
import CannedResponsesUI from "components/rich-editor/canned-responses-ui";
import LinkUI, { IUpdateParams } from "components/rich-editor/link-ui";
import { MutationKeys } from "constants/mutation-keys";
import { ResponsiveBreakpoint } from "constants/responsive-breakpoint";
import { EventPlace } from "helpers/analytics";
import { CannedResponseInputMethod } from "helpers/canneds";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { IStoreState } from "interfaces/storeState";
import { AttachmentContext, useAttachments } from "routes/tickets/ticket/message-box/richtext/AttachmentProvider";
import { selectAgents } from "store/entities/agents";
import { TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE } from "store/entities/transaction/constants";
import { selectTicketAgentsTyping } from "store/features/ticket-activity/selectors";
import { withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";
import { plural } from "utils/string";

import { ICannedResponseTagValue, ICannedResponsesProps } from "../canned-responses";

import { CollapseButton } from "./CollapseButton";
import { Toolbar } from "./Toolbar";
import { CannedResponses } from "./canned-responses";
import { Mentions } from "./mentions/Mentions";

interface IStyledWrapperProps {
  disabled: boolean;
  privateMode: boolean;
  sizeExceeded: boolean;
}

const StyledWrapper = styled.div<IStyledWrapperProps>`
  border: 1px solid
    ${({ sizeExceeded }): string =>
      withVar(sizeExceeded ? DesignToken.BorderBasicNegative : DesignToken.BorderBasicPrimary)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  transition: background-color ${withVar(TransitionDurationToken.Fast2)};
  cursor: text;

  @media (max-width: ${ResponsiveBreakpoint.Mobile}px) {
    padding: 0 ${withVar(SpacingToken.Spacing2)} ${withVar(SpacingToken.Spacing2)};
    margin: ${withVar(SpacingToken.Spacing2)};
  }

  ${({ privateMode }): SerializedStyles =>
    privateMode
      ? css`
          background: ${withVar(DesignToken.SurfaceInvertSecondary)};
          color: ${withVar(DesignToken.ContentInvertPrimary)};
        `
      : css``};

  ${({ disabled }): SerializedStyles =>
    disabled
      ? css`
          cursor: default;
          pointer-events: none;
          opacity: 0.5;
        `
      : css``};
`;

const StyledTextAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px 12px;

  @media (max-width: ${ResponsiveBreakpoint.Mobile}px) {
    padding: 9px 8px;
  }
`;

const StyledToolbarWrapper = styled.div`
  padding: var(${SpacingToken.Spacing2}) var(${SpacingToken.Spacing2}) var(${SpacingToken.Spacing3});

  @media (max-width: ${ResponsiveBreakpoint.Mobile}px) {
    padding: var(${SpacingToken.Spacing1});
  }
`;

interface ITextareaWrapperProps {
  privateMode: boolean;
  collapsed: boolean;
}

const StyledRichTextAreaWrapper = styled.div<ITextareaWrapperProps>`
  .rich-text-editor {
    background: transparent;
    border: 0;
    color: ${({ privateMode }): string =>
      withVar(privateMode ? DesignToken.ContentInvertPrimary : DesignToken.ContentBasicPrimary)};
    font-size: 14px;
    line-height: 21px;
    min-height: 5em;
    min-height: 3lh;
    margin: 0;
    max-height: ${({ collapsed }): string => (collapsed ? "5em" : "40vh")};
    max-height: ${({ collapsed }): string => (collapsed ? "3lh" : "40vh")};
    transition: max-height ${withVar(TransitionDurationToken.Fast2)} ease-in-out;
    overflow-y: auto;
    outline: none;
    padding: 0;
    resize: none;
    width: 100%;
    white-space: pre-wrap;

    &::placeholder {
      color: ${({ privateMode }): string =>
        withVar(privateMode ? DesignToken.ContentInvertDisabled : DesignToken.ContentBasicDisabled)};
    }

    @media (max-width: ${ResponsiveBreakpoint.Mobile}px) {
      min-height: unset;
      height: auto;
      max-height: 84px;
    }
  }

  a {
    text-decoration: underline;
    color: ${({ privateMode }): string =>
      withVar(privateMode ? DesignToken.ContentInvertPrimary : DesignToken.ActionPrimaryDefault)};
  }

  code {
    font-size: 14px;
  }

  ol {
    list-style: decimal;
    list-style-position: inside;
    margin: 10px 20px;
  }

  ul {
    list-style: disc;
    list-style-position: inside;
    margin: 10px 20px;
  }

  li {
    margin: initial;
    padding: initial;
    line-height: 1.4;
  }

  h1 {
    font-size: 32px;
    line-height: 40px;
  }

  h2 {
    font-size: 24px;
    line-height: 28px;
  }

  h3 {
    font-size: 20px;
    line-height: 24px;
  }

  h4 {
    font-size: 16px;
    line-height: 24px;
  }

  h5 {
    font-size: 15px;
    line-height: 22px;
  }

  h6 {
    font-size: 12px;
    line-height: 20px;
  }

  .mention {
    ${({ privateMode }): string => (privateMode ? "font-weight: 600;" : "")}
  }

  blockquote {
    padding: 0.5em 1em;
    background-color: ${withVar(DesignToken.SurfaceSecondaryDefault)};
    margin: 0.5em 0;
  }
`;

export interface IProps {
  value: Descendant[];
  placeholder: string;
  isMutating: boolean;
  privateMode?: boolean;
  cannedResponsesByShortcuts?: ICannedResponsesProps["items"];
  maxRows?: number;
  enableAttachments?: boolean;
  disabled?: boolean;
  topNodes?: React.ReactNode;
  leftNodes?: React.ReactNode;
  rightNodes?: React.ReactNode;
  sendButtonLabel?: string;
  sendButtonDisabled?: boolean;
  sendButtonLoading?: boolean;
  ticketId?: string;
  onModeChange?: React.MouseEventHandler;
  onSendButtonClick(e: React.MouseEvent<HTMLButtonElement>): void;
  cannedResponsePoint: PointRef | null;
  mentionsPoint: PointRef | null;
  onTextareaRefChange?: (textarea: HTMLElement) => void;
  textareaRef?: (textarea: HTMLElement) => void;
  onKeyDown?(e: React.KeyboardEvent<HTMLDivElement>): void;
  onFocus?(e: React.FocusEvent<HTMLDivElement>): void;
  onBlur?(e: React.FocusEvent<HTMLDivElement>): void;
  onResize?(): void;
  onInputMouseDown?(e: React.MouseEvent<HTMLDivElement>): void;
  onCannedResponseUse?(id: string, response: string, method: CannedResponseInputMethod): void;
  onAttachmentClick?(): void;
  onEmojiClick?(): void;
  onSendButtonMouseDown?(e: React.MouseEvent<HTMLButtonElement>): void;
  textFormattingVisible: boolean;
  onTextFormattingClick: React.EventHandler<React.MouseEvent<HTMLDivElement>>;
  aiTextEnhancementsVisible: boolean;
  onAiFormattingClick: React.EventHandler<React.MouseEvent<HTMLDivElement>>;
}

interface IForwardedMessageBoxProps {
  editor: Editor;
  handleMentionClick: () => void;
  handleCannedResponsesClick: () => void;
  cannedResponseValue: string | null;
  handleCloseCannedResponses: () => void;
  handleCannedResponseSelect: (
    cannedResponseTagValue: ICannedResponseTagValue,
    method: CannedResponseInputMethod
  ) => void;
  linkUpdateParams: IUpdateParams | null;
  setLinkUpdateParams: React.Dispatch<React.SetStateAction<IUpdateParams | null>>;
  cannedWindowRef: React.RefObject<HTMLDivElement>;
}

export const SizeExceededText = styled.p`
  color: ${withVar(DesignToken.ContentBasicNegative)};
  font-size: 14px;
  line-height: 20px;
  text-align: right;
`;

const AgentTypingIndicator: React.FC<{ ticketID: string }> = ({ ticketID }) => {
  const agents = useSelector(selectAgents);
  const typingAgentIDs = useSelector((state: IStoreState) => selectTicketAgentsTyping(state, ticketID));
  const typingAgents = typingAgentIDs.map(id => agents[id]).filter(Boolean);

  return typingAgents.length ? (
    <Text fontSize="14px" paddingBottom="4px" color="primaryColor60">
      {typingAgents.map(a => a.name).join(", ")} {plural(typingAgents.length, "is", "are")} typing…
    </Text>
  ) : null;
};

interface IMessageBoxState {
  collapsed: boolean;
  shouldShowCollapseButton: boolean;
}

export class MessageBox extends React.PureComponent<
  IProps & IWithBreakpointsProps & IForwardedMessageBoxProps,
  IMessageBoxState
> {
  static contextType = AttachmentContext;
  // @ts-expect-error TS(2612): Property 'context' will overwrite the base propert... Remove this comment to see the full error message
  context!: React.ContextType<typeof AttachmentContext>;

  static defaultProps = {
    cannedResponsesByShortcuts: [],
    disabled: false,
    sendButtonDisabled: false,
    sendButtonLoading: false,
    privateMode: false,
    enableAttachments: false,
    textareaRef: noop,
    onKeyDown: noop,
    onFocus: noop,
    onBlur: noop,
    onResize: noop,
    onInputMouseDown: noop,
    onCannedResponseClick: noop,
    onAttachmentClick: noop,
    onEmojiClick: noop,
    onSendButtonMouseDown: noop,
  };

  state: IMessageBoxState = {
    collapsed: false,
    shouldShowCollapseButton: false,
  };

  textareaRef = React.createRef<HTMLElement>();
  resizeObserver: ResizeObserver | null = null;
  mutationObserver: MutationObserver | null = null;

  componentDidMount(): void {
    const editorElement = document.querySelector(".rich-text-editor");
    const setCollapseButtonVisibility = (el: HTMLElement): void => {
      const { scrollHeight, clientHeight } = el;
      const { minHeight } = window.getComputedStyle(el);

      const computedHeight = parseInt(minHeight.replace("px", ""));
      const isCollapsible = clientHeight > computedHeight; // RTE is larger than its minHeight
      const isScrollable = scrollHeight > clientHeight; // RTE is currently scrollable

      const shouldShowCollapseButton = isCollapsible || isScrollable;

      this.setState(prevState => ({
        shouldShowCollapseButton,
        collapsed: shouldShowCollapseButton ? prevState.collapsed : false,
      }));
    };

    if (editorElement) {
      this.resizeObserver = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry) {
          setCollapseButtonVisibility(entry.target as HTMLElement);
        }
      });

      this.mutationObserver = new MutationObserver(entries => {
        const entry = entries[0];
        if (entry) {
          setCollapseButtonVisibility(entry.target as HTMLElement);
        }
      });

      this.mutationObserver.observe(editorElement, {
        childList: true,
        attributeFilter: ["scrollHeight"],
      });
      this.resizeObserver.observe(editorElement);
    }
  }

  componentWillUnmount(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  componentDidCatch(error: Error): void {
    Sentry.withScope(scope => {
      const { editor } = this.props;
      scope.setExtra(
        "editorPayload",
        JSON.stringify({
          value: editor.children,
          selection: editor.selection,
          history: editor.history,
        })
      );
      throw error;
    });
  }
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  handleLinkInsert = (event: any) => {
    const { editor, setLinkUpdateParams } = this.props;
    const [linkEntry] = Editor.nodes(editor, {
      match: node => Element.isElement(node) && node.type === "link",
      universal: true,
    });

    let linkRange = null;
    if (linkEntry) {
      linkRange = Editor.range(editor, Editor.start(editor, linkEntry[1]), Editor.end(editor, linkEntry[1]));
      Transforms.select(editor, linkRange);
    }

    event.preventDefault();
    if (!ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.start(editor, []));
    }

    if (editor.selection) {
      const domRange = ReactEditor.toDOMRange(editor, editor.selection);
      if (domRange?.startContainer?.parentElement) {
        scrollIntoView(domRange?.startContainer?.parentElement, { scrollMode: "if-needed" });
      }
    }

    setLinkUpdateParams({
      // @ts-expect-error TS(2345)
      selection: Editor.rangeRef(editor, editor.selection, { affinity: "inward" }),
      ...(linkRange ? { focusDisplayName: false } : { focusDisplayName: true }),
    });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  handleHotkeys = (event: any, pluginKeyDown: any) => {
    if (isHotkey("mod+k", event.nativeEvent)) {
      this.handleLinkInsert(event);
    } else {
      pluginKeyDown(event);
    }
  };

  componentDidUpdate(prevProps: IProps): void {
    if (prevProps.privateMode !== this.props.privateMode) {
      this.props.setLinkUpdateParams(null);
    }

    if (prevProps.sendButtonLoading !== this.props.sendButtonLoading) {
      this.props.setLinkUpdateParams(null);
    }
  }

  render(): JSX.Element {
    const {
      editor,
      isMobile,
      cannedResponsesByShortcuts,
      mentionsPoint,
      placeholder,
      cannedResponseValue,
      sendButtonDisabled,
      sendButtonLoading,
      sendButtonLabel,
      topNodes,
      leftNodes,
      rightNodes,
      onFocus,
      onAttachmentClick,
      onSendButtonClick,
      onSendButtonMouseDown,
      textFormattingVisible,
      onTextFormattingClick,
      aiTextEnhancementsVisible,
      onAiFormattingClick,
      handleCloseCannedResponses,
      handleCannedResponseSelect,
      linkUpdateParams,
      setLinkUpdateParams,
      onModeChange,
      cannedWindowRef,
      ticketId,
      onBlur,
    } = this.props;

    const { collapsed, shouldShowCollapseButton } = this.state;
    const transactionSizeExceeded = this.context.attachmentsSize() > TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE;
    const disabled = Boolean(this.props.disabled);
    const privateMode = Boolean(this.props.privateMode);
    const enableAttachments = Boolean(this.props.enableAttachments);

    return (
      <>
        {/* @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message */}
        <AgentTypingIndicator ticketID={ticketId} />
        {transactionSizeExceeded && <SizeExceededText>Your message cannot exceed 10 MB</SizeExceededText>}
        <ThemeProvider theme={legacyTheme}>
          <div ref={this.textareaRef as React.RefObject<HTMLDivElement>} css={{ position: "relative" }}>
            <StyledWrapper
              disabled={disabled}
              privateMode={privateMode}
              data-testid="richtext-editor-wrapper"
              onClick={() => {
                ReactEditor.focus(editor);
              }}
              sizeExceeded={this.context.attachmentsSize() > TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE}
            >
              <StyledTextAreaWrapper>
                {topNodes}
                {this.props.isMutating && (
                  <Loader
                    css={{
                      position: "fixed",
                      alignSelf: "center",
                      opacity: 1,
                      borderRadius: "4px",
                      padding: "4px",
                      zIndex: 100000,
                    }}
                  />
                )}
                <StyledRichTextAreaWrapper privateMode={privateMode} collapsed={this.state.collapsed}>
                  <RichTextArea
                    id="rich-text-area"
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    className="rich-text-editor"
                    spellCheck
                    onKeyDown={this.handleHotkeys}
                  />
                  <LinkUI
                    eventPlace={EventPlace.Tickets}
                    params={linkUpdateParams}
                    setParams={setLinkUpdateParams}
                    containerRef={this.textareaRef.current}
                  />
                  <CannedResponsesUI
                    cannedWindowRef={cannedWindowRef}
                    cannedResponses={cannedResponsesByShortcuts ?? []}
                  >
                    <CannedResponses
                      containerRef={cannedWindowRef}
                      searchPhrase={cannedResponseValue ?? ""}
                      items={cannedResponsesByShortcuts ?? []}
                      onClose={handleCloseCannedResponses}
                      onSelect={handleCannedResponseSelect}
                      textareaWithCannedResponsesRef={this.textareaRef as React.RefObject<HTMLDivElement>}
                    />
                  </CannedResponsesUI>
                  <Mentions
                    point={mentionsPoint}
                    onSelect={() => {
                      if (!privateMode) {
                        // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
                        // eslint-disable-next-line @typescript-eslint/no-empty-function
                        onModeChange({ stopPropagation() {}, preventDefault() {} } as React.MouseEvent<
                          HTMLElement,
                          MouseEvent
                        >);
                      }
                    }}
                  />
                </StyledRichTextAreaWrapper>
              </StyledTextAreaWrapper>
              <StyledToolbarWrapper>
                <Toolbar
                  disabled={disabled}
                  sendButtonDisabled={sendButtonDisabled}
                  sendButtonLoading={sendButtonLoading}
                  sendButtonLabel={sendButtonLabel}
                  privateMode={privateMode}
                  attachmentsEnabled={enableAttachments}
                  mobile={isMobile}
                  leftNodes={leftNodes}
                  rightNodes={rightNodes}
                  onLinkInsert={this.handleLinkInsert}
                  textFormattingVisible={textFormattingVisible}
                  onTextFormattingClick={onTextFormattingClick}
                  aiTextEnhancementsVisible={aiTextEnhancementsVisible}
                  onAiFormattingClick={onAiFormattingClick}
                  onCannedResponseClick={this.props.handleCannedResponsesClick}
                  onMentionClick={ticketId ? this.props.handleMentionClick : undefined}
                  onAttachmentClick={onAttachmentClick ?? noop}
                  onSendButtonClick={onSendButtonClick}
                  // @ts-expect-error TS(2322): Type '((e: MouseEvent<HTMLButtonElement, MouseEven... Remove this comment to see the full error message
                  onSendButtonMouseDown={onSendButtonMouseDown}
                />
              </StyledToolbarWrapper>
            </StyledWrapper>
            <CollapseButton
              isVisible={shouldShowCollapseButton}
              isCollapsed={collapsed}
              onClick={() => this.setState({ collapsed: !collapsed })}
            />
          </div>
        </ThemeProvider>
      </>
    );
  }
}

const RichMessageBox: React.FC<IProps & IWithBreakpointsProps> = ({
  onCannedResponseUse,
  onSendButtonClick,
  disabled,
  ...restProps
}) => {
  const editor = useSlate();
  const { mergeTransaction } = useAttachments();
  const [linkUpdateParams, setLinkUpdateParams] = useState<IUpdateParams | null>(null);
  const cannedWindowRef = useRef<HTMLDivElement>(null);
  const isMutating = useIsMutating([MutationKeys.TextEnhancement]);

  const handleCannedResponseSelect = (
    cannedResponseTagValue: ICannedResponseTagValue,
    method: CannedResponseInputMethod
  ): void => {
    const { text, richTextObj, id: cannedResponseID, attachments } = cannedResponseTagValue;

    const cannedResponseRange = editor.cannedResponsesAPI.range();

    if (cannedResponseRange) {
      const isRichTextCanned = richTextObj !== null;
      if (isRichTextCanned) {
        if (attachments && attachments.length > 0) {
          mergeTransaction({ cannedResponseID });
        }
        Transforms.delete(editor, {
          at: cannedResponseRange,
          distance: 1,
        });
        // @ts-expect-error TS(2322)
        Transforms.insertFragment(editor, cloneDeep(richTextObj));
      } else {
        Transforms.insertText(editor, text, {
          at: cannedResponseRange,
        });
      }
    }

    editor.cannedResponsesAPI.stop();

    onCannedResponseUse?.(cannedResponseID, text, method);
  };

  const handleCannedResponsesClose = (): void => {
    editor.cannedResponsesAPI.stop();
  };

  const handleSendButtonClick: React.EventHandler<React.MouseEvent<HTMLButtonElement>> = event => {
    editor.cannedResponsesAPI.stop();
    onSendButtonClick(event);
  };

  const handleCannedResponsesClick = (): void => {
    if (!ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.start(editor, [0]));
      editor.insertText("#");
    } else {
      editor.insertText("#");
    }
  };

  const handleMentionClick = (): void => {
    if (!ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.start(editor, []));
    }
    setTimeout(() => editor.insertText("@"), 100);
  };

  const cannedResponseValue = (): string => {
    const cannedResponseRange = editor.cannedResponsesAPI.range();
    if (!cannedResponseRange) {
      return "";
    }

    return cannedResponseRange ? Editor.string(editor, cannedResponseRange).slice(1) : "";
  };

  return (
    <>
      <MessageBox
        {...restProps}
        disabled={disabled || Boolean(isMutating)}
        isMutating={Boolean(isMutating)}
        editor={editor}
        handleCannedResponsesClick={handleCannedResponsesClick}
        handleCannedResponseSelect={handleCannedResponseSelect}
        handleMentionClick={handleMentionClick}
        onSendButtonClick={handleSendButtonClick}
        cannedResponseValue={cannedResponseValue()}
        handleCloseCannedResponses={handleCannedResponsesClose}
        linkUpdateParams={linkUpdateParams}
        setLinkUpdateParams={setLinkUpdateParams}
        cannedWindowRef={cannedWindowRef}
      />
    </>
  );
};

export default withBreakpoints(RichMessageBox);
